import React, { useEffect, useState } from "react";
import { Plus, X, Trash2, Edit3 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../contexts/LanguageContext";
import { api } from "../../lib/api";

const empty = { title_tr: "", title_en: "", slug: "", excerpt_tr: "", excerpt_en: "", content_tr: "", content_en: "", category: "General", tags: [], featured_image: "", author: "Vakar Team", status: "published" };

const BlogManager = () => {
  const { t } = useLang();
  const [posts, setPosts] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/blog");
    setPosts(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (edit.id) {
        await api.put(`/admin/blog/${edit.id}`, edit);
      } else {
        await api.post("/admin/blog", edit);
      }
      toast.success(t.admin.saved);
      setEdit(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t.admin.confirm)) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success(t.admin.deleted);
    load();
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex justify-between items-center" style={{ marginBottom: 24 }}>
        <h1 className="font-display" style={{ fontSize: "2.5rem", fontWeight: 300, color: "var(--warm-off-white)" }}>{t.admin.nav.blog}</h1>
        <button data-testid="blog-new" onClick={() => setEdit({ ...empty })} className="font-mono" style={{ padding: "10px 18px", background: "var(--bordeaux)", color: "var(--warm-off-white)", border: "none", fontSize: "0.7rem", letterSpacing: "0.18em", textTransform: "uppercase", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}><Plus size={14} /> {t.admin.new}</button>
      </div>
      <div style={{ background: "var(--rich-black-light)", border: "1px solid rgba(139,32,53,0.25)" }}>
        {posts.map((p, i) => (
          <div key={p.id} style={{ padding: 20, borderTop: i === 0 ? "none" : "1px solid rgba(248,244,240,0.06)", display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 16, alignItems: "center" }}>
            <div>
              <div style={{ color: "var(--warm-off-white)", fontWeight: 500 }}>{p.title_tr}</div>
              <div className="font-mono" style={{ fontSize: "0.75rem", color: "rgba(248,244,240,0.5)", marginTop: 4 }}>{p.title_en} • /{p.slug}</div>
            </div>
            <span className="font-mono" style={{ fontSize: "0.7rem", color: "var(--gold)" }}>{p.category}</span>
            <span className="font-mono" style={{ fontSize: "0.65rem", letterSpacing: "0.15em", padding: "4px 10px", border: "1px solid rgba(248,244,240,0.2)", color: "rgba(248,244,240,0.7)", textTransform: "uppercase" }}>{p.status}</span>
            <div className="flex" style={{ gap: 8 }}>
              <button data-testid={`blog-edit-${p.id}`} onClick={() => setEdit({ ...p })} style={{ background: "transparent", border: "none", color: "var(--gold)", cursor: "pointer", padding: 4 }}><Edit3 size={16} /></button>
              <button data-testid={`blog-delete-${p.id}`} onClick={() => remove(p.id)} style={{ background: "transparent", border: "none", color: "rgba(248,244,240,0.5)", cursor: "pointer", padding: 4 }}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {posts.length === 0 && <div style={{ padding: 24, color: "rgba(248,244,240,0.5)" }}>—</div>}
      </div>

      {edit && (
        <div data-testid="blog-editor" onClick={() => setEdit(null)} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(10,10,10,0.92)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--rich-black-light)", border: "1px solid rgba(201,151,58,0.35)", maxWidth: 920, width: "100%", padding: 40, maxHeight: "90vh", overflowY: "auto", position: "relative" }}>
            <button onClick={() => setEdit(null)} style={{ position: "absolute", top: 16, right: 16, background: "transparent", border: "none", color: "var(--warm-off-white)" }}><X size={22} /></button>
            <h2 className="font-display" style={{ fontSize: "1.75rem", color: "var(--warm-off-white)", marginBottom: 24 }}>{edit.id ? t.admin.edit : t.admin.new}</h2>
            <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <input placeholder="Title TR" className="input-line" value={edit.title_tr} onChange={(e) => setEdit({ ...edit, title_tr: e.target.value })} />
              <input placeholder="Title EN" className="input-line" value={edit.title_en} onChange={(e) => setEdit({ ...edit, title_en: e.target.value })} />
              <input placeholder="Slug (optional)" className="input-line" value={edit.slug || ""} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} />
              <input placeholder="Category" className="input-line" value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} />
              <input placeholder="Featured Image URL" className="input-line" value={edit.featured_image} onChange={(e) => setEdit({ ...edit, featured_image: e.target.value })} style={{ gridColumn: "1 / -1" }} />
              <textarea placeholder="Excerpt TR" rows={3} className="input-line" value={edit.excerpt_tr} onChange={(e) => setEdit({ ...edit, excerpt_tr: e.target.value })} />
              <textarea placeholder="Excerpt EN" rows={3} className="input-line" value={edit.excerpt_en} onChange={(e) => setEdit({ ...edit, excerpt_en: e.target.value })} />
              <textarea placeholder="Content TR (HTML allowed)" rows={8} className="input-line" value={edit.content_tr} onChange={(e) => setEdit({ ...edit, content_tr: e.target.value })} style={{ gridColumn: "1 / -1" }} />
              <textarea placeholder="Content EN (HTML allowed)" rows={8} className="input-line" value={edit.content_en} onChange={(e) => setEdit({ ...edit, content_en: e.target.value })} style={{ gridColumn: "1 / -1" }} />
              <select className="input-line" value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} style={{ appearance: "none" }}>
                <option value="published" style={{ background: "var(--rich-black)" }}>Published</option>
                <option value="draft" style={{ background: "var(--rich-black)" }}>Draft</option>
              </select>
              <input placeholder="Author" className="input-line" value={edit.author} onChange={(e) => setEdit({ ...edit, author: e.target.value })} />
            </div>
            <div className="flex" style={{ gap: 12, marginTop: 32 }}>
              <button data-testid="blog-save" onClick={save} className="font-mono" style={{ padding: "12px 28px", background: "var(--gold)", color: "var(--rich-black)", border: "none", fontSize: "0.7rem", letterSpacing: "0.2em", textTransform: "uppercase", cursor: "pointer" }}>{t.admin.save}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManager;
